import { Fragment, useId, useState, type KeyboardEvent } from 'react'
import logo from '../assets/doron-logo.png'
import { MARK_PATH, PRODUCT_NAME, expansionWords } from '../brand'

/**
 * The DORON mark and the signed-out shell it sits in.
 *
 * **Every pixel is local**, which is a requirement rather than a preference:
 * TECHNICAL_DESIGN.md §4 has the bundle served by the appliance itself, on a
 * network that may have no route out at all. So the logo is imported and emitted
 * into the bundle, the backdrop is drawn rather than fetched, and the type is
 * the system stack — a webfont from a CDN is a sign-in page that renders in
 * Times New Roman at the customer, discovered by them and not by us.
 *
 * **The backdrop is decorative and says so.** It is a market line with its area
 * fill and a few candlesticks: the subject matter is what this product is for,
 * and a screen reader reading out a chart that carries no data would be
 * announcing information that does not exist. `aria-hidden`, and no title.
 *
 * Restraint is deliberate. This is the front door of a compliance system for
 * asset managers, and the surrounding screens are plain because a limit is
 * easier to read on a plain surface. The brand belongs on the way in, not
 * across the screen where somebody is checking a threshold.
 */
export function BrandLogo({ className = 'brand-logo' }: { className?: string }) {
  return <img src={logo} alt="DORON" className={className} />
}

/** Positions, named rather than positional: a bare tuple of four numbers is a
 *  row nobody can read back, and these are picked by eye. */
const CANDLES = [
  { x: 120, top: 380, bottom: 300, body: 60 },
  { x: 190, top: 340, bottom: 280, body: 70 },
  { x: 260, top: 360, bottom: 250, body: 55 },
  { x: 330, top: 300, bottom: 230, body: 80 },
  { x: 400, top: 320, bottom: 210, body: 50 },
  { x: 470, top: 260, bottom: 190, body: 75 },
  { x: 540, top: 280, bottom: 170, body: 45 },
  { x: 610, top: 230, bottom: 150, body: 70 },
  { x: 680, top: 250, bottom: 140, body: 55 },
  { x: 750, top: 200, bottom: 120, body: 65 },
  { x: 820, top: 220, bottom: 110, body: 50 },
  { x: 890, top: 170, bottom: 90, body: 70 },
  { x: 960, top: 190, bottom: 80, body: 48 },
  { x: 1030, top: 150, bottom: 70, body: 60 },
]

/**
 * The Anubandh mark — a handshake.
 *
 * *Anubandh* means a contract: the binding agreement between a manager and their
 * client, which is the document every limit in this system comes from. A
 * handshake is that agreement in one glyph, and it is the product's own mark
 * rather than DORON's, which is the house it comes from.
 *
 * Google's Material Symbols `handshake` (filled), under the Apache License 2.0
 * — see NOTICE.md. Drawn by people who draw icons: two attempts at sketching one
 * from primitives read as flat slabs, because a handshake is mostly overlap and
 * foreshortening and neither survives being built out of rectangles. Recoloured
 * to the house gradient and otherwise untouched.
 *
 * Inlined rather than loaded from an icon font, for §4's reason and one more: a
 * font that fails to load renders as a box, and a box is indistinguishable from
 * a bug.
 */
export function AnubandhMark({
  className = 'product-mark',
  tone = 'brand',
}: {
  className?: string
  /** `light` is the glyph in white, for sitting on the gradient tile. */
  tone?: 'brand' | 'light'
}) {
  return (
    <svg
      className={className}
      viewBox="0 -960 960 960"
      role="img"
      aria-label={PRODUCT_NAME}
      focusable="false"
    >
      {tone === 'brand' && (
        <defs>
          <linearGradient id="mark-brand" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#0d2b6b" />
            <stop offset="100%" stopColor="#0f9b8e" />
          </linearGradient>
        </defs>
      )}
      <path fill={tone === 'brand' ? 'url(#mark-brand)' : '#ffffff'} d={MARK_PATH} />
    </svg>
  )
}

/**
 * What the five letters stand for, shown when somebody points at the name.
 *
 * **A card of our own rather than a `title` attribute.** The browser's tooltip
 * waits a second, cannot be styled, never appears for a keyboard and never on a
 * touch screen. This one opens on hover and on focus, stays open while the
 * pointer moves onto it, and closes on Escape — WCAG 1.4.13's three conditions
 * for content that appears on hover. The initials are picked out, so the card
 * answers *why GUARD* as well as *what is GUARD*.
 *
 * Hidden with `visibility` rather than unmounted: a closed card then adds
 * nothing to the accessible name of the heading it sits in, and
 * `aria-describedby` still reaches it, because a hidden element referenced
 * directly is still read as a description.
 */
function useNameCard() {
  const id = useId()
  const [open, setOpen] = useState(false)
  const trigger = {
    tabIndex: 0,
    'aria-describedby': id,
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    },
  }
  const card = (
    <span role="tooltip" id={id} className="name-tip" data-open={open}>
      {expansionWords().map(({ initial, rest }, i) => (
        <Fragment key={i}>
          {i > 0 && ' '}
          <span className={initial ? 'name-word name-word-initial' : 'name-word'}>
            {initial + rest}
          </span>
        </Fragment>
      ))}
    </span>
  )
  return { trigger, card }
}

/**
 * The product lockup: the handshake on its tile, then the name, with the name
 * card on hover.
 *
 * **The glyph sits white on the house gradient rather than being drawn in it.**
 * At header size a gradient glyph on a white bar read as a smudge beside DORON's
 * full-colour logo; on a tile it holds its own as a mark. **The name is set in
 * wide-tracked heavy capitals** because it is an acronym: at the body face's
 * ordinary tracking five capitals close up into a word that looks shouted
 * rather than named.
 */
export function Wordmark({ size = 'header' }: { size?: 'header' | 'title' }) {
  const { trigger, card } = useNameCard()
  return (
    <span className={`lockup lockup-${size}`} {...trigger}>
      <span className="mark-tile" aria-hidden="true">
        <AnubandhMark className="mark-glyph" tone="light" />
      </span>
      <span className="lockup-name">{PRODUCT_NAME}</span>
      {card}
    </span>
  )
}

/** The name inside a sentence, with the same card. */
export function ProductName() {
  const { trigger, card } = useNameCard()
  return (
    <span className="product-name" {...trigger}>
      {PRODUCT_NAME}
      {card}
    </span>
  )
}


function MarketBackdrop() {
  return (
    <svg
      className="backdrop"
      viewBox="0 0 1200 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="brand-sky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0d2b6b" />
          <stop offset="55%" stopColor="#12569a" />
          <stop offset="100%" stopColor="#0f9b8e" />
        </linearGradient>
        <linearGradient id="brand-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2fd6b0" stopOpacity="0.38" />
          <stop offset="100%" stopColor="#2fd6b0" stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect width="1200" height="600" fill="url(#brand-sky)" />

      {/* A rule grid, at the weight of graph paper rather than of a chart. */}
      <g stroke="#ffffff" strokeOpacity="0.07" strokeWidth="1">
        {[100, 200, 300, 400, 500].map((y) => (
          <line key={y} x1="0" y1={y} x2="1200" y2={y} />
        ))}
        {[150, 300, 450, 600, 750, 900, 1050].map((x) => (
          <line key={x} x1={x} y1="0" x2={x} y2="600" />
        ))}
      </g>

      {/* Candlesticks, sparse and low-contrast: texture, not a reading. */}
      <g stroke="#ffffff" strokeOpacity="0.16" fill="#ffffff" fillOpacity="0.1">
        {CANDLES.map(({ x, top, bottom, body }) => (
          <g key={x}>
            <line x1={x} y1={top} x2={x} y2={bottom} />
            <rect x={x - 9} y={top + 20} width="18" height={body} />
          </g>
        ))}
      </g>

      {/* The line itself, and its area. Upward, because it is the front door. */}
      <path
        d="M0 470 L100 440 L200 455 L300 400 L400 415 L500 350 L600 370 L700 305
           L800 320 L900 255 L1000 275 L1100 215 L1200 235 L1200 600 L0 600 Z"
        fill="url(#brand-area)"
      />
      <path
        d="M0 470 L100 440 L200 455 L300 400 L400 415 L500 350 L600 370 L700 305
           L800 320 L900 255 L1000 275 L1100 215 L1200 235"
        fill="none"
        stroke="#2fd6b0"
        strokeOpacity="0.75"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function BrandShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="brand-shell">
      <MarketBackdrop />
      <div className="brand-panel">
        {children}
        <p className="brand-footer">
          DORON Tech Solutions · GUARD — Guideline &amp; Unified Automated Rules
          Decisioning
        </p>
      </div>
    </div>
  )
}
